import { useEffect, useMemo, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { buildTheme } from './app/theme'
import { NAVIGATION } from './app/navigation'
import AppShell from './app/AppShell'
import Login from './pages/Login'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import SetPasswordPage from './pages/SetPasswordPage'
import Dashboard from './pages/Dashboard'
import Placeholder from './pages/Placeholder'
import SessionsPage from './pages/SessionsPage'
import UsersPage from './pages/administrare/UsersPage'
import RolesPage from './pages/administrare/RolesPage'
import WorkPointsPage from './pages/administrare/WorkPointsPage'
import AuditPage from './pages/administrare/AuditPage'
import ArticlesPage from './pages/nomenclatoare/ArticlesPage'
import SetsPage from './pages/nomenclatoare/SetsPage'
import UnitsPage from './pages/nomenclatoare/UnitsPage'
import TaxePage from './pages/financiar/TaxePage'
import FacturiPage from './pages/financiar/FacturiPage'
import IncasariPlatiPage from './pages/financiar/IncasariPlatiPage'
import CashflowPage from './pages/financiar/CashflowPage'
import ReconciliereBancaraPage from './pages/financiar/ReconciliereBancaraPage'
import CompanySettingsPage from './pages/administrare/CompanySettingsPage'
import ClientsPage from './pages/comercial/ClientsPage'
import PreturiPage from './pages/comercial/PreturiPage'
import CereriPage from './pages/comercial/CereriPage'
import OfertePage from './pages/comercial/OfertePage'
import ComenziPage from './pages/comercial/ComenziPage'
import SuppliersPage from './pages/achizitii/SuppliersPage'
import PurchaseRequestsPage from './pages/achizitii/PurchaseRequestsPage'
import ContractsPage from './pages/achizitii/ContractsPage'
import PurchaseOrdersPage from './pages/achizitii/PurchaseOrdersPage'
import GoodsReceiptsPage from './pages/achizitii/GoodsReceiptsPage'
import SupplierInvoicesPage from './pages/achizitii/SupplierInvoicesPage'
import WarehousesPage from './pages/stocuri/WarehousesPage'
import StockPage from './pages/stocuri/StockPage'
import StockMovementsPage from './pages/stocuri/StockMovementsPage'
import LotsPage from './pages/stocuri/LotsPage'
import TransfersPage from './pages/stocuri/TransfersPage'
import InventoryPage from './pages/stocuri/InventoryPage'
import PickingListsPage from './pages/logistica/PickingListsPage'
import ShipmentsPage from './pages/logistica/ShipmentsPage'
import RetetarPage from './pages/productie/RetetarPage'
import RoutesPage from './pages/productie/RoutesPage'
import OrdinePage from './pages/productie/OrdinePage'
import PlanPage from './pages/productie/PlanPage'
import CalitatePage from './pages/productie/CalitatePage'
import EquipmentPage from './pages/mentenanta/EquipmentPage'
import WorkOrdersPage from './pages/mentenanta/WorkOrdersPage'
import MaintenanceCalendarPage from './pages/mentenanta/MaintenanceCalendarPage'
import AprobarileMelePage from './pages/aprobari/AprobarileMelePage'
import FluxuriAprobarePage from './pages/administrare/FluxuriAprobarePage'
import CheltuieliMelePage from './pages/cheltuieli/CheltuieliMelePage'
import AvansuriMelePage from './pages/cheltuieli/AvansuriMelePage'
import DeconturiMelePage from './pages/cheltuieli/DeconturiMelePage'
import DeconturiToatePage from './pages/cheltuieli/DeconturiToatePage'
import RegistruActivePage from './pages/active/RegistruActivePage'
import IntrariActivePage from './pages/active/IntrariActivePage'
import IesiriActivePage from './pages/active/IesiriActivePage'
import AmortizarePage from './pages/active/AmortizarePage'
import AlertePage from './pages/AlertePage'
import NotificarileMelePage from './pages/NotificarileMelePage'
import NotificationRulesPage from './pages/administrare/NotificationRulesPage'
import KpiPage from './pages/planificare/KpiPage'
import RapoarteCatalogPage from './pages/RapoarteCatalogPage'
import './i18n'

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
})

type Mode = 'light' | 'dark'

/**
 * Căile care primesc un ecran real în locul lui Placeholder. Trebuie excluse
 * din bucla generică de mai jos — altfel React Router ar avea două <Route>
 * pe aceeași cale statică, ordine ambiguă.
 */
const REAL_SCREENS: Record<string, React.ComponentType> = {
  '/administrare/utilizatori': UsersPage,
  '/administrare/roluri': RolesPage,
  '/administrare/puncte-lucru': WorkPointsPage,
  '/administrare/sesiuni': SessionsPage,
  '/administrare/audit': AuditPage,
  '/nomenclatoare/articole': ArticlesPage,
  '/nomenclatoare/seturi': SetsPage,
  '/nomenclatoare/um': UnitsPage,
  '/financiar/taxe': TaxePage,
  '/financiar/facturi': FacturiPage,
  '/financiar/incasari-plati': IncasariPlatiPage,
  '/financiar/cashflow': CashflowPage,
  '/financiar/reconciliere': ReconciliereBancaraPage,
  '/cheltuieli/proprii': CheltuieliMelePage,
  '/cheltuieli/avansuri': AvansuriMelePage,
  '/cheltuieli/deconturi': DeconturiMelePage,
  '/cheltuieli/toate': DeconturiToatePage,
  '/active/registru': RegistruActivePage,
  '/active/intrari': IntrariActivePage,
  '/active/iesiri': IesiriActivePage,
  '/active/amortizare': AmortizarePage,
  '/alerte': AlertePage,
  '/planificare/kpi': KpiPage,
  '/rapoarte': RapoarteCatalogPage,
  '/administrare/parametri': CompanySettingsPage,
  '/comercial/clienti': ClientsPage,
  '/comercial/preturi': PreturiPage,
  '/comercial/cereri': CereriPage,
  '/comercial/oferte': OfertePage,
  '/comercial/comenzi': ComenziPage,
  '/achizitii/furnizori': SuppliersPage,
  '/achizitii/cereri': PurchaseRequestsPage,
  '/achizitii/contracte': ContractsPage,
  '/achizitii/comenzi': PurchaseOrdersPage,
  '/achizitii/receptii': GoodsReceiptsPage,
  '/achizitii/facturi': SupplierInvoicesPage,
  '/stocuri/depozite': WarehousesPage,
  '/stocuri/stoc': StockPage,
  '/stocuri/miscari': StockMovementsPage,
  '/stocuri/loturi': LotsPage,
  '/stocuri/transferuri': TransfersPage,
  '/stocuri/inventar': InventoryPage,
  '/logistica/picking': PickingListsPage,
  '/logistica/expeditii': ShipmentsPage,
  '/productie/retetar': RetetarPage,
  '/productie/rute': RoutesPage,
  '/productie/ordine': OrdinePage,
  '/productie/plan': PlanPage,
  '/productie/calitate': CalitatePage,
  '/mentenanta/echipamente': EquipmentPage,
  '/mentenanta/ordine-lucru': WorkOrdersPage,
  '/mentenanta/calendar': MaintenanceCalendarPage,
  '/aprobari': AprobarileMelePage,
  '/administrare/fluxuri-aprobare': FluxuriAprobarePage,
  '/notificarile-mele': NotificarileMelePage,
  '/administrare/reguli-notificare': NotificationRulesPage,
}

function Routed({ mode, onToggleMode }: { mode: Mode; onToggleMode: () => void }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Routes>
      {/* Publice — accesibile fără autentificare. Un utilizator deja autentificat
          care ajunge aici (link vechi, tab separat) e trimis la dashboard. */}
      <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={user ? <Navigate to="/" replace /> : <SetPasswordPage mode="reset" />} />
      <Route path="/accept-invitation" element={user ? <Navigate to="/" replace /> : <SetPasswordPage mode="invite" />} />

      {!user ? (
        <Route path="*" element={<Login />} />
      ) : (
        <Route element={<AppShell mode={mode} onToggleMode={onToggleMode} />}>
          <Route index element={<Dashboard />} />
          {Object.entries(REAL_SCREENS).map(([path, Component]) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          {/* Restul ecranelor din meniu nu sunt scrise încă — arată explicit ce
              urmează, în locul unei pagini goale. */}
          {NAVIGATION.flatMap((g) => g.items)
            .filter((i) => i.path !== '/' && !(i.path in REAL_SCREENS))
            .map((i) => <Route key={i.key} path={i.path} element={<Placeholder />} />)}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
  )
}

function Themed() {
  const { i18n } = useTranslation()
  const [mode, setMode] = useState<Mode>(
    () => (localStorage.getItem('cipral.theme') as Mode | null) ?? 'light',
  )

  useEffect(() => {
    try { localStorage.setItem('cipral.theme', mode) } catch { /* mod privat */ }
  }, [mode])

  const theme = useMemo(() => buildTheme(mode, i18n.language), [mode, i18n.language])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routed mode={mode} onToggleMode={() => setMode((m) => (m === 'light' ? 'dark' : 'light'))} />
    </ThemeProvider>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Themed />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
